import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  useCreateOrganisationMembership,
  useListOrganisationMembers,
  useListUsers,
  UserSummary,
} from '../../../../../app/ApiGen';
import { UserAvatar } from '../../../../../components/Avatar/User';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { SelectableItem, SelectionList } from '../../../../../components/SelectionList';
import { FormActions, Loading } from '../../../../../design-system';
import type { SubState } from '../../../../../store/types';
import { getSelectableUsers } from '../../../../../utils/users/form';

interface SelectUserContext {
  allUsers: UserSummary[];
  organisationMembers: UserSummary[];
  organisation: SubState<'organisation'>;
}

const SelectUser: React.FC<SelectUserContext> = ({ allUsers, organisationMembers, organisation }) => {
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const { push } = useHistory();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const addUserMembership = useCreateOrganisationMembership({ organisationId: organisation.id });
  const onUserSelect = React.useCallback(
    (user: SelectableItem) => {
      setSelectedUserIds([user.id]);
    },
    [setSelectedUserIds],
  );

  const addDriversToOrg = React.useCallback(async () => {
    await Promise.all(
      selectedUserIds.map(async (userId) => {
        await addUserMembership.mutate({
          data: {
            type: 'memberships',
            attributes: { isDefault: false, role: 2 },
            relationships: {
              organisation: { data: { id: organisation.id, type: 'organisations' } },
              user: { data: { id: userId, type: 'users' } },
            },
          },
        });
      }),
    );
  }, [addUserMembership, organisation.id, selectedUserIds]);

  const onAddUser = React.useCallback(() => {
    setSubmitting(true);
    addDriversToOrg()
      .then(() => {
        enqueueSnackbar('User added to organisation', { variant: 'success' });
        push(`/organisations/${organisation.slug}/users`);
      })
      .catch((e) => {
        setSubmitting(false);
        errorSnackbar('Unable to add user to organisation', closeSnackbar, enqueueSnackbar, e);
      });
  }, [addDriversToOrg, closeSnackbar, enqueueSnackbar, organisation.slug, push]);

  const selectableUsers: SelectableItem[] = getSelectableUsers(allUsers, organisationMembers);

  return (
    <SelectionList
      getImage={({ title, color, image }) => <UserAvatar embedded user={{ name: title, color, avatar: image }} />}
      items={selectableUsers}
      label="user"
      onSelectItem={onUserSelect}
      upperActions={
        <Button
          component={Link}
          to={`/organisations/${organisation.slug}/create/user`}
          color="primary"
          variant={selectableUsers.length > 0 ? 'outlined' : 'contained'}
        >
          Invite new user
        </Button>
      }
      lowerActions={
        <FormActions>
          <Button
            color="primary"
            disabled={submitting || selectedUserIds.length === 0}
            onClick={onAddUser}
            variant="contained"
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Add user'}
          </Button>
        </FormActions>
      }
    />
  );
};

export const SelectUserStep: React.FC<{ organisation: SubState<'organisation'> }> = ({ organisation }) => {
  const allUsers = useListUsers({});
  const organisationMembers = useListOrganisationMembers({ organisationId: organisation.id });

  if (allUsers.loading || organisationMembers.loading) {
    return <Loading />;
  }

  if (!allUsers.data || !organisationMembers.data || allUsers.error || organisationMembers.error) {
    return (
      <>
        {(allUsers.error || !allUsers.data) && (
          <DataErrorHandler description="Unable to load all users" error={allUsers.error} refetch={allUsers.refetch} />
        )}
        {(organisationMembers.error || !organisationMembers.data) && (
          <DataErrorHandler
            description="Unable to load organisation users"
            error={organisationMembers.error}
            refetch={organisationMembers.refetch}
          />
        )}
      </>
    );
  }

  return (
    <SelectUser
      allUsers={allUsers.data.data}
      organisationMembers={organisationMembers.data.included}
      organisation={organisation}
    />
  );
};
